/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { IScanLine } from '../../../../../common/src/domain/drone/IScanLine';
import { UtProject } from '../../../../../common/src/domain/drone/utProject/utProjectTableSchema';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchDelete, fetchGet, fetchPost } from '../../../store/fetch';
import { AssetTypes } from '../../../utils/enums/AssetTypes';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import { IConnectProps } from '../../../utils/IConnectProps';
import { isCustomerVersion } from '../../../utils/isCustomerVersion';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    EditPanelSection,
    EditPanelSwitch,
    PanelBody,
    PanelButtonLink,
    Tray,
} from '../../elements';
import { Component } from '../../utils/Component';
import { DisplayMessage } from '../../utils/displayError';
import { DropzoneMaxSizeMessage } from '../../utils/dropzone/DropzoneMaxSizeMessage';
import { showSavedToast } from '../../utils/toastMessages';
import { AssetDropzone } from '../Common/AssetDropzone';
import { IAsset } from '../Interfaces/IAsset';
import { SelectModel3dSurvey } from '../SelectModel3dSurvey';
import { SurveyEditPointClouds } from './SurveyEditPointClouds';

interface ISurveyEditPointcloudProps extends ITranslateProps, IConnectProps {
    surveyId: string;
    inspectionStandardType: InspectionStandardTypes;
}

type UtProjectView = Pick<UtProject, 'useScanLines' | 'name' | 'id' | 'maxScanLinePositions'>;

interface ISurveyEditPointcloudState {
    objectId?: undefined | string;
    objectName?: undefined | string;
    objectCompositionIsVisible?: undefined | boolean;
    isDOMLight?: undefined | boolean;
    isAnnotationsDefaultVisible?: undefined | boolean;
    submitting: boolean;
    failed: boolean;
    scanLineCoordinates: IScanLine[] | null;
    projects: UtProjectView[];
    plyAsset?: undefined | IAsset;
    plyUploadError: string | null;
    ifcAsset?: undefined | IAsset;
    ifcUploadError: string | null;
    shapesInModel: IProjectShape[] | null;
    reloadIFrameId: string;
    hasPointClouds: boolean;
    model3dSurveyId: string | null;
}

function mapStateToProps(_state: any) {
    return {};
}

interface IProjectShape {
    radiusTop: number;
    radiusBottom: number;
    height: number;
    positionY: number;
    scanLines: number;
    positions: number;
    projectId: string;
}

function ProjectShapesEdit(props: {
    projects: UtProjectView[];
    projectShapes: null | IProjectShape[];
    onChange(projectShapes: IProjectShape[]): void;
}) {
    const t = useTranslate();

    function change(projectId: string, key: string, value: string) {
        const newProjectShapes = props.projectShapes === null ? [] : [...props.projectShapes];

        let item = newProjectShapes.find((i) => i.projectId === projectId);
        if (item === undefined) {
            item = {
                radiusTop: 0,
                radiusBottom: 0,
                height: 0,
                positionY: 0,
                scanLines: 0,
                positions: 0,
                projectId,
            };

            newProjectShapes.push(item);
        }
        const parsedValue = isNaN(parseFloat(value)) ? value : parseFloat(value);

        (item as any)[key] = parsedValue;

        props.onChange(newProjectShapes);
    }

    function shapeBy(projectId: string) {
        if (props.projectShapes === null) {
            return undefined;
        }
        return props.projectShapes.find((i) => i.projectId === projectId);
    }
    return (
        <>
            <table className="table mb10">
                <thead>
                    <tr className="bg-primary">
                        <th rowSpan={2}>{t('common.name')}</th>
                        <th colSpan={4}>Shape</th>
                        <th colSpan={2}>Measurements</th>
                    </tr>
                    <tr className="bg-primary">
                        <th>Radius Top</th>
                        <th>Radius Bottom</th>
                        <th>Height</th>
                        <th>Y Position</th>
                        <th>Scanlines</th>
                        <th>Positions</th>
                    </tr>
                </thead>
                <tbody>
                    {props.projects
                        .filter((i) => i.useScanLines)
                        .map((project, index) => (
                            <tr key={index}>
                                <td>{project.name}</td>
                                <td>
                                    <input
                                        style={{ width: 80 }}
                                        type="number"
                                        value={shapeBy(project.id) ? shapeBy(project.id)!.radiusTop : 0}
                                        onChange={(e) => change(project.id, 'radiusTop', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        style={{ width: 80 }}
                                        type="number"
                                        value={shapeBy(project.id) ? shapeBy(project.id)!.radiusBottom : 0}
                                        onChange={(e) => change(project.id, 'radiusBottom', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        style={{ width: 80 }}
                                        type="number"
                                        value={shapeBy(project.id) ? shapeBy(project.id)!.height : 0}
                                        onChange={(e) => change(project.id, 'height', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        style={{ width: 80 }}
                                        type="number"
                                        value={shapeBy(project.id) ? shapeBy(project.id)!.positionY : 0}
                                        onChange={(e) => change(project.id, 'positionY', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        style={{ width: 80 }}
                                        type="number"
                                        value={shapeBy(project.id) ? shapeBy(project.id)!.scanLines : 0}
                                        onChange={(e) => change(project.id, 'scanLines', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        style={{ width: 80 }}
                                        type="number"
                                        value={shapeBy(project.id) ? shapeBy(project.id)!.positions : 0}
                                        onChange={(e) => change(project.id, 'positions', e.target.value)}
                                    />
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </>
    );
}

class SurveyEditPointcloud extends Component<ISurveyEditPointcloudProps, ISurveyEditPointcloudState> {
    private pointCloudIFrame: HTMLIFrameElement | undefined;

    constructor(props: ISurveyEditPointcloudProps) {
        super(props);

        this.state = {
            submitting: false,
            failed: false,
            isAnnotationsDefaultVisible: true,
            scanLineCoordinates: null,
            projects: [],
            shapesInModel: null,
            reloadIFrameId: uuid(),
            hasPointClouds: false,
            model3dSurveyId: null,
            ifcUploadError: null,
            plyUploadError: null,
        };
    }

    public override componentDidMount() {
        void this.load();
    }

    public getValidatorTypes(): any {
        return {};
    }

    public getValidatorData() {
        return {
            objectId: this.state.objectId,
            isDOMLight: this.state.isDOMLight,
            isAnnotationsDefaultVisible: this.state.isAnnotationsDefaultVisible,
            scanLineCoordinates: this.state.scanLineCoordinates,
            shapesInModel: this.state.shapesInModel,
            model3dSurveyId: this.state.model3dSurveyId,
        };
    }

    public override render() {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const potreeUrl = `/assets/pointcloud/viewer/v18.html?objectId=${this.state.objectId}&surveyId=${this.props.surveyId}&annotationsDefaultVisible=${this.state.isAnnotationsDefaultVisible}&reloadIFrameId=${this.state.reloadIFrameId}`;

        return (
            <Tray>
                <form method="post" action="/" id="admin-form">
                    <EditPanel bottomMargin={true} noPadding={true}>
                        <PanelBody>
                            {((this.state.hasPointClouds || this.state.plyAsset || this.state.ifcAsset) && (
                                <div className="" id="3DModel">
                                    <div className="p5">
                                        <iframe
                                            ref={(ref) => {
                                                this.pointCloudIFrame = ref!;
                                            }}
                                            src={`${potreeUrl}&hideSidebarTools=true`}
                                            scrolling="no"
                                            title="Pointcloud editor"
                                            id="pointcloud-iframe"
                                            width="100%"
                                            height={400}
                                            frameBorder={0}
                                        />
                                    </div>
                                    <div className="pull-right">
                                        <PanelButtonLink
                                            onClick={() => this.openInWindow(potreeUrl)}
                                            small={true}
                                            primary={false}
                                            icon="fa fa-external-link"
                                            text={this.props.t('survey.open3dViewer')}
                                        />
                                    </div>
                                </div>
                            )) || <p className="text-center">- {this.props.t('survey.noPointcloud')} -</p>}
                        </PanelBody>
                    </EditPanel>

                    {this.state.objectId &&
                        isCustomerVersion('zxy') &&
                        [InspectionStandardTypes.Free, InspectionStandardTypes.Nen2767, InspectionStandardTypes.Fmeca].includes(
                            this.props.inspectionStandardType,
                        ) && (
                            <EditPanel bottomMargin={true} noPadding={true}>
                                <CollapsiblePanel heading={{ dark: true, text: this.props.t('survey.pointClouds'), initialIsClosed: true }}>
                                    <SelectModel3dSurvey
                                        surveyId={this.props.surveyId}
                                        objectId={this.state.objectId}
                                        onUpdated={async () => this.reloadPointCloud()}
                                        model3dSurveyId={this.state.model3dSurveyId}
                                        onChange={async (model3dSurveyId) => this.setState({ model3dSurveyId })}
                                        onSave={async () => this.saveClicked()}
                                    />
                                </CollapsiblePanel>
                            </EditPanel>
                        )}

                    {(!isCustomerVersion('zxy') || this.props.inspectionStandardType === InspectionStandardTypes.Model3d) && (
                        <EditPanel bottomMargin={true} noPadding={true}>
                            <CollapsiblePanel heading={{ dark: true, text: this.props.t('common.settings'), initialIsClosed: true }}>
                                <PanelBody isForm={true}>
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={6}>
                                            <EditPanelSwitch
                                                name="isDOMLight"
                                                placeholder={this.props.t('survey.isDOMLight')}
                                                value={this.state.isDOMLight}
                                                handleChange={this.handleChange.bind(this)}
                                            />
                                        </EditPanelCell>
                                    </EditPanelRow>

                                    {!isCustomerVersion('zxy') && (
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSwitch
                                                    name="isAnnotationsDefaultVisible"
                                                    placeholder={this.props.t('survey.isAnnotationsDefaultVisible')}
                                                    value={this.state.isAnnotationsDefaultVisible}
                                                    handleChange={this.handleChange.bind(this)}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    )}

                                    <EditPanelButtons>
                                        <EditPanelButton
                                            small={true}
                                            text={this.props.t('common.save')}
                                            onClick={async () => this.saveClicked()}
                                            primary
                                        />
                                    </EditPanelButtons>
                                </PanelBody>
                            </CollapsiblePanel>
                        </EditPanel>
                    )}

                    {(!isCustomerVersion('zxy') || this.props.inspectionStandardType === InspectionStandardTypes.Model3d) && this.state.objectId && (
                        <EditPanel bottomMargin={true} noPadding={true}>
                            <CollapsiblePanel heading={{ dark: true, text: this.props.t('survey.pointClouds'), initialIsClosed: true }}>
                                <SurveyEditPointClouds
                                    surveyId={this.props.surveyId}
                                    objectId={this.state.objectId}
                                    onUpdated={async () => this.reloadPointCloud()}
                                />
                            </CollapsiblePanel>
                        </EditPanel>
                    )}

                    {this.state.objectId && (
                        <>
                            {/* PLY */}
                            <EditPanel bottomMargin={true} noPadding={true}>
                                <CollapsiblePanel heading={{ dark: true, text: this.props.t('survey.plyModel'), initialIsClosed: true }}>
                                    <PanelBody isForm={true}>
                                        {this.state.plyUploadError && (
                                            <DisplayMessage
                                                message={
                                                    this.state.plyUploadError === 'Dropzone: FILE_TOO_BIG'
                                                        ? this.props.t('asset.errorFileTooBig')
                                                        : this.state.plyUploadError
                                                }
                                            />
                                        )}
                                        {this.state.plyAsset === undefined && (
                                            <div className="pr20 pl20">
                                                <AssetDropzone
                                                    assetType={AssetTypes.PlyModel}
                                                    acceptedFiles=".ply"
                                                    onComplete={async () => this.loadPlyAsset()}
                                                    onError={(errorCode) => void this.setState({ plyUploadError: errorCode })}
                                                    objectId={this.state.objectId}
                                                    surveyId={this.props.surveyId}
                                                    onSending={() => void this.setState({ plyUploadError: null })}
                                                    onQueueComplete={() => console.log('onQueueComplete')}
                                                    maxFiles={1}
                                                    autoProcessQueue={true}
                                                    maxFileSizeMb={100}
                                                />
                                                <DropzoneMaxSizeMessage maxSizeMb={100} fileExtension=".ply" />
                                            </div>
                                        )}
                                        {this.state.plyAsset && (
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={6}>{this.state.plyAsset.name}</EditPanelCell>
                                                <EditPanelCell rowCount={6}>
                                                    <button
                                                        type="button"
                                                        onClick={async () => this.removePlyAsset()}
                                                        className="btn btn-default btn-xs"
                                                    >
                                                        {' '}
                                                        <i className="fa fa-trash-o"></i>{' '}
                                                    </button>
                                                </EditPanelCell>
                                            </EditPanelRow>
                                        )}
                                    </PanelBody>
                                </CollapsiblePanel>
                            </EditPanel>

                            {/* IFC */}
                            <EditPanel bottomMargin={true} noPadding={true}>
                                <CollapsiblePanel heading={{ dark: true, text: this.props.t('survey.ifcModel'), initialIsClosed: true }}>
                                    <PanelBody isForm={true}>
                                        {this.state.ifcUploadError && (
                                            <DisplayMessage
                                                message={
                                                    this.state.ifcUploadError === 'Dropzone: FILE_TOO_BIG'
                                                        ? this.props.t('asset.errorFileTooBig')
                                                        : this.state.ifcUploadError
                                                }
                                            />
                                        )}
                                        {this.state.ifcAsset === undefined && (
                                            <div className="pr20 pl20">
                                                <AssetDropzone
                                                    assetType={AssetTypes.IfcModel}
                                                    acceptedFiles=".ifc"
                                                    onComplete={async () => this.loadIfcAsset()}
                                                    onError={(errorCode) => void this.setState({ ifcUploadError: errorCode })}
                                                    objectId={this.state.objectId}
                                                    surveyId={this.props.surveyId}
                                                    onSending={() => void this.setState({ ifcUploadError: null })}
                                                    onQueueComplete={() => console.log('onQueueComplete')}
                                                    maxFiles={1}
                                                    autoProcessQueue={true}
                                                    maxFileSizeMb={250}
                                                />
                                                <DropzoneMaxSizeMessage maxSizeMb={250} fileExtension=".ifc" />
                                            </div>
                                        )}
                                        {this.state.ifcAsset && (
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={6}>{this.state.ifcAsset.name}</EditPanelCell>
                                                <EditPanelCell rowCount={6}>
                                                    <button
                                                        type="button"
                                                        onClick={async () => this.removeIfcAsset()}
                                                        className="btn btn-default btn-xs"
                                                    >
                                                        {' '}
                                                        <i className="fa fa-trash-o"></i>{' '}
                                                    </button>
                                                </EditPanelCell>
                                            </EditPanelRow>
                                        )}
                                    </PanelBody>
                                </CollapsiblePanel>
                            </EditPanel>
                        </>
                    )}

                    {isCustomerVersion('ronik') && (
                        <>
                            <EditPanel bottomMargin={true} noPadding={true}>
                                <CollapsiblePanel heading={{ dark: true, text: 'Oiltank shape', initialIsClosed: true }}>
                                    <PanelBody isForm={true}>
                                        <EditPanelSection>
                                            <ProjectShapesEdit
                                                projectShapes={this.state.shapesInModel}
                                                onChange={this.handleProjectShapesChange}
                                                projects={this.state.projects}
                                            />
                                        </EditPanelSection>
                                    </PanelBody>
                                </CollapsiblePanel>
                            </EditPanel>
                        </>
                    )}
                </form>
            </Tray>
        );
    }

    private async loadSurvey() {
        const url = `/api/surveys/${this.props.surveyId}`;
        const surveyResponse = await fetchGet(url);
        surveyResponse.json.surveryedOn = new Date(surveyResponse.json.surveryedOn);
        await this.setState(surveyResponse.json);
        await this.setState({ reloadIFrameId: uuid() });
        await this.loadObject(surveyResponse.json.objectId);
    }

    private async load() {
        await this.loadSurvey();

        if (isCustomerVersion('ronik')) {
            const projectsResponse = await fetchGet(`/api/surveys/${this.props.surveyId}/utprojects`);
            await this.setState({ projects: projectsResponse.json.items });
        }
        await this.loadPlyAsset();
        await this.loadIfcAsset();
    }

    private async loadIfcAsset() {
        const result = await fetchGet('/api/assets', {
            surveyId: this.props.surveyId,
            types: `${AssetTypes.IfcModel}`,
        });
        if (result.json.length > 0) {
            await this.setState({ ifcAsset: result.json[0] });
        }
        await this.reloadPointCloud();
    }

    private async removeIfcAsset() {
        if (this.state.ifcAsset) {
            await fetchDelete(`/api/surveys/${this.props.surveyId}/assets/${this.state.ifcAsset.id}`);
            await this.setState({ ifcAsset: undefined });
            await this.reloadPointCloud();
        }
    }

    private async loadPlyAsset() {
        const result = await fetchGet('/api/assets', {
            surveyId: this.props.surveyId,
            types: `${AssetTypes.PlyModel}`,
        });
        if (result.json.length > 0) {
            await this.setState({ plyAsset: result.json[0] });
        }
        await this.reloadPointCloud();
    }

    private async removePlyAsset() {
        if (this.state.plyAsset) {
            await fetchDelete(`/api/surveys/${this.props.surveyId}/assets/${this.state.plyAsset.id}`);
            await this.setState({ plyAsset: undefined });
            await this.reloadPointCloud();
        }
    }

    private readonly handleProjectShapesChange = async (shapesInModel: IProjectShape[]) => {
        await this.setState({ shapesInModel });
    };

    private async handleChange(value: any, stateKey: string) {
        const newState = {} as any;
        newState[stateKey] = value;
        await this.setState(newState);
    }

    private async loadObject(objectId: string) {
        const url = `/api/objects/${objectId}?fields=name,compositionIsVisible`;
        const result = await fetchGet(url);
        await this.setState({ objectName: result.json.name, objectCompositionIsVisible: result.json.compositionIsVisible });
    }

    private async saveClicked() {
        const url = `/api/surveys/${this.props.surveyId}`;

        let data = {} as any;

        data = this.getValidatorData() as any;

        await fetchPost(url, {}, data);
        showSavedToast(this.props.t);
        await this.load();
        await this.reloadPointCloud();
    }

    private async reloadPointCloud() {
        if (!this.state.hasPointClouds) {
            await this.loadSurvey();
        }

        if (this.pointCloudIFrame && this.pointCloudIFrame.contentWindow) {
            this.pointCloudIFrame.contentWindow.location.reload();
        }
    }

    private openInWindow(url: string) {
        window.open(`${url}&hideSidebarTools=false`, undefined, 'height=600,width=800,status=yes,resizable=yes,toolbar=no,menubar=no,location=no');
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(SurveyEditPointcloud));
